import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import polyline from "@mapbox/polyline";
import ContentBody from "./ContentBody";

const API = process.env.REACT_APP_BACKEND_URL;

// Bounding box Nederland + viewport
const NL = { minLat: 50.70, maxLat: 53.65, minLon: 3.30, maxLon: 7.30 };
const W = 700;
const H = 900;

// Equirectangulaire projectie met breedtegraad-correctie
const COS = Math.cos(52.2 * Math.PI / 180);
const LON_RANGE = (NL.maxLon - NL.minLon) * COS;
const LAT_RANGE = NL.maxLat - NL.minLat;

function toSvgXY([lon, lat]) {
  const x = ((lon - NL.minLon) * COS / LON_RANGE) * W;
  const y = (1 - (lat - NL.minLat) / LAT_RANGE) * H;
  return `${x.toFixed(1)},${y.toFixed(1)}`;
}

function routePoints(coords) {
  return (coords || []).map(toSvgXY).join(" ");
}

function activityPoints(summaryPolyline) {
  return polyline.decode(summaryPolyline)
    .map(([lat, lon]) => toSvgXY([lon, lat]))
    .join(" ");
}

export default function Kleurplaat() {
  const { pageKey } = useParams() ?? "404";
  const [routes, setRoutes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/kleurplaat`)
      .then(res => res.json())
      .then(setContent)
      .catch(err => console.error(`Error loading ${pageKey} content`, err));
    fetch(`${API}/event-routes`)
      .then(r => r.json())
      .then(data => setRoutes(Array.isArray(data) ? data.filter(r => r.coordinates?.length > 1) : []))
      .catch(() => { });

    fetch(`${API}/activities`)
      .then(r => r.json())
      .then(data => setActivities(Array.isArray(data) ? data.filter(a => a.summary_polyline) : []))
      .catch(() => { });


  }, []);

  const totalKm = routes.reduce((s, r) => s + (r.distanceKm || 0), 0);

  if (!content) return null;

  return (
    <div className="pane">
      <h1>{content.title}</h1>
      <ContentBody data={content} />

      <div className="kleurplaat-container">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          xmlns="http://www.w3.org/2000/svg"
          className="kleurplaat-svg"
          aria-label="Kleurplaat Nederland"
        >
          <defs>
            {/*
              Masker-logica:
              - Witte achtergrond → overlay is overal zichtbaar (afbeelding verborgen)
              - Zwarte paden (routes + activiteiten) → overlay wordt transparant op die plek
                → afbeelding schijnt erdoorheen (het "krassen")
            */}
            <mask id="kras-mask">
              <rect width={W} height={H} fill="white" />
              {routes.map(r => (
                <polyline
                  key={`er-${r._id}`}
                  points={routePoints(r.coordinates)}
                  stroke="black"
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
              {activities.map(a => (
                <polyline
                  key={`ac-${a._id}`}
                  points={activityPoints(a.summary_polyline)}
                  stroke="black"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
            </mask>
          </defs>

          {/* Achtergrond: de te onthullen afbeelding */}
          <image
            href="/media/glove-web.png"
            x="0"
            y="0"
            width={W}
            height={H}
            preserveAspectRatio="xMidYMid meet"
          />

          {/* Zilvergrijze overlay — verdwijnt langs gekrasten routes */}
          <rect
            width={W}
            height={H}
            fill="#b0b0b0"
            mask="url(#kras-mask)"
          />
        </svg>

        <p className="kleurplaat-stats note">
          {routes.length} etappes · {totalKm} km contour
          {activities.length > 0 && ` · ${activities.length} activiteiten gekrast`}
        </p>
      </div>
    </div>
  );
}
